import { PieChartWithPaddingAngleWrapper } from '@modules/charts/components/PieChartWithPaddingAngleWrapper';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import {
  getFieldNameFromEqlField,
  getMetadataQBFieldFromEqlField,
} from '@modules/common/utils';
import { QuantifyReportStatusWidget } from '@modules/quantify/components/QuantifyReportStatusWidget';
import {
  useFetchQuantifyReport,
  useGetQuantifyChartData,
  useIsSingleRecordQuery,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Box } from '@mui/material';
import { round } from 'lodash';
import { TimeRange } from 'v2/core/entities';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

interface QuantifyPieChartProps {
  shouldShowAddToFilter?: boolean;
  shouldDisableInteraction?: boolean;
  minHeight?: number;
  rootStyles?: React.CSSProperties;
  shouldShowLabel?: boolean;
  layout?: 'horizontal' | 'vertical';
  shouldShowLegendsInNewRow?: boolean;
  shouldDisableAnimation?: boolean;
}

export function QuantifyPieChart({
  shouldShowAddToFilter = true,
  shouldDisableInteraction = false,
  minHeight = 300,
  rootStyles,
  shouldShowLabel = true,
  layout,
  shouldShowLegendsInNewRow,
  shouldDisableAnimation = false,
}: QuantifyPieChartProps) {
  const data = useGetQuantifyChartData();
  const duration = useQuantifyStore(({ query }) => query.duration);
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const formulaID = useQuantifyStore(({ query }) => query.formulaID);
  const whereClause = useQuantifyStore(
    ({ query }) => query.whereClauseConfig?.whereClause ?? EmptyWhereClause,
  );
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );

  const isSingleRecordQuery = useIsSingleRecordQuery();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const totalVolume = round(
    data.reduce((sum, item) => sum + item.value, 0),
    2,
  );

  const fetchQuantifyReport = useFetchQuantifyReport();

  const getLabel = () => {
    if (!measuredAsCriteria) {
      // Ideally measuredAsCriteria should always be present
      // and we should never hit this
      return {
        subtitle: String(totalVolume),
      };
    }

    const [caption1, caption2] = measuredAsCriteria.displayName.split(' ');
    return {
      subtitle: String(totalVolume),
      caption1,
      caption2,
    };
  };

  if (!data.length) {
    return <QuantifyReportStatusWidget height={minHeight} />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        p: 2,
        display: 'flex',
        alignItems: 'center',
        ...rootStyles,
      }}
    >
      <PieChartWithPaddingAngleWrapper
        data={data}
        dataKey="value"
        labelKey="label"
        label={getLabel()}
        baseWhereClause={whereClause}
        fieldName={groupBy ? getFieldNameFromEqlField(groupBy) : undefined}
        qbField={groupBy ? getMetadataQBFieldFromEqlField(groupBy) : undefined}
        displayName={
          groupBy
            ? getFormattedDisplayNameForEqlField({
                eqlField: groupBy,
                shouldRemoveFileUploadPrefix,
                isUnifiedFieldEnabled,
              })
            : undefined
        }
        dateRange={duration as TimeRange}
        minHeight={minHeight}
        shouldShowAddToFilter={shouldShowAddToFilter}
        shouldDisableInteraction={shouldDisableInteraction}
        shouldShowLabel={shouldShowLabel}
        layout={layout}
        shouldShowLegendsInNewRow={shouldShowLegendsInNewRow}
        shouldDisableAnimation={shouldDisableAnimation}
        onMergeComplete={fetchQuantifyReport}
        formulaID={formulaID}
        refetch={fetchQuantifyReport}
        measuredAsCriteria={measuredAsCriteria}
        isSingleRecordQuery={isSingleRecordQuery}
      />
    </Box>
  );
}
